import styled from 'styled-components'
import { fonts, size } from '../../../styles/GlobalVariables'
import { ISlideLayout } from '../../VerticalViewer/groupTurn'
import { RING_THIRDS, RING_WATCHED, ringMask } from './StoriesRail.style'

/**
 * Figma "D_Home Page with 13 Shorts", Option B (24189:370562, 24189:371154):
 * the episodes beside the 914px player as 210x373.424 cards, 114px from it
 * and 60px apart. All of it is a share of the player, so a smaller window
 * keeps the design's proportions.
 */
const STAGE_W = 514
const STAGE_H = 914
const CARD_W = 210
const CARD_H = 373.424

export const CARDS_LAYOUT: ISlideLayout = {
  first: (STAGE_W / 2 + 114 + CARD_W / 2) / STAGE_W,
  step: (CARD_W + 60) / STAGE_W,
  size: CARD_W / STAGE_W,
}

/**
 * The cards need a window where a card is wide enough for its 90px bubble and
 * label (112px, a 490px player, 580px of window). In a shorter one (a big
 * browser zoom) there are no cards: the chevrons and the cube stay.
 */
export const CARDS_MEDIA = `(min-width: ${size.regular}px) and (min-height: 580px)`

/** the design's 1920 canvas around the stage, each side, in stage widths */
const CANVAS_SIDE = (1920 - STAGE_W) / 2 / STAGE_W

/**
 * Behind the stage (a card slides under the player on its way in), as wide
 * as the design's canvas: a wider window shows black, not more cards.
 * Dimmed once in an ad, as the side buttons.
 */
export const CardsRow = styled.div<{ $disabled: boolean }>`
  display: none;

  @media ${CARDS_MEDIA} {
    display: block;
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    clip-path: inset(0 ${-CANVAS_SIDE * 100}%);
    opacity: ${props => (props.$disabled ? 0.4 : 1)};
  }
`

/** the one playing waits hidden behind the player until the slide takes it out */
export const Card = styled.button<{ $current: boolean }>`
  pointer-events: auto;
  position: absolute;
  top: ${((STAGE_H - CARD_H) / 2 / STAGE_H) * 100}%;
  width: ${(CARD_W / STAGE_W) * 100}%;
  height: ${(CARD_H / STAGE_H) * 100}%;
  padding: 0;
  border: 0;
  border-radius: 4px;
  overflow: hidden;
  background: none;
  font-family: ${fonts.narkis};
  color: #fff;
  cursor: pointer;
  visibility: ${props => (props.$current ? 'hidden' : 'visible')};
  /* a layer of its own from the start: made at the first slide, the layers
     took that slide's longest frame from 167 to 100ms (Edge, 5 runs) */
  will-change: transform;
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  /* the hover edge, inside the card over everything */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0 0 0 1.5px rgba(255, 255, 255, 0.6);
    opacity: 0;
    pointer-events: none;
  }

  @media (hover: hover) {
    &:hover:not(:disabled)::after {
      opacity: 1;
    }

    &:hover:not(:disabled) > [data-card-shade] {
      --shade-to: 0.55;
    }
  }

  &:active:not(:disabled) > [data-card-shade] {
    --shade-from: 0.25;
    --shade-to: 0.9;
  }

  &:disabled {
    cursor: default;
  }
`

/** the frame the episode opens on, at 60% over the backdrop */
export const CardImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.6;
`

/**
 * Short Preview Card 24501:52861: one gradient, clear to 38.7%, 80% black at
 * 93.75%; the card's hover and press move its two ends
 */
export const CardShade = styled.span.attrs({ 'data-card-shade': '' })`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, var(--shade-from, 0)) 38.702%,
    rgba(0, 0, 0, var(--shade-to, 0.8)) 93.75%
  );
`

/** the rail's bubble at its phone size, centred 0.79px low as drawn */
export const CardStory = styled.span`
  position: absolute;
  top: calc(50% + 0.79px);
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 90px;
`

/** the rail's thirds (Short Preview Card, 2026-10-19): a 4px band, 3px to the 76px picture; watched 4.091 / 2.909 */
export const CardRing = styled.span<{ $watched: boolean }>`
  position: relative;
  display: block;
  box-sizing: border-box;
  width: 90px;
  height: 90px;
  padding: 7px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props => (props.$watched ? RING_WATCHED : RING_THIRDS)};
    ${props => ringMask(props.$watched ? '4.091px' : '4px')}
  }

  img {
    display: block;
    width: 76px;
    height: 76px;
    border-radius: 50%;
    object-fit: cover;
    background-color: #fff;
  }
`
